import { ReactNode } from 'react';

import { showModal } from 'modal.react.js';
import type { GroupBase, Options, PropsValue } from 'react-select-5';

import Modal from 'components/shared/GlobalModalArea/Modal';
import logger from 'utils/logger';

import area from '../../../GlobalModalArea/area';
import { ConfirmationModal } from './ConfirmationModal';
import { Controls, Props } from './types';

// function cleanValue<Option>(value: PropsValue<Option>): Options<Option>;
// https://github.com/JedWatson/react-select/blob/06e34882638d1526b9f5a1238bb567a3e9460ce5/packages/react-select/src/utils.ts#L67
function cleanValue<Option>(value: PropsValue<Option>): Options<Option> {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'object' && value !== null) return [value as Option];
  return [];
}

export function useCreateOption<
  _Option = unknown,
  _IsMulti extends boolean = boolean,
  _Group extends GroupBase<_Option> = GroupBase<_Option>,
>(controls: Controls<_Option, _IsMulti, _Group>, props: Props<_Option, _IsMulti, _Group>) {
  const { selectRef } = controls;
  const { onCreateOption: pOnCreateOption, creatable, getNewOptionData, name } = props;

  if (!creatable) return { selectRef, onCreateOption: undefined };

  const { creatingRequiresConfirmation, creatingConfirmationMessage } = props;

  const onCreateOption =
    (creatingRequiresConfirmation || pOnCreateOption) &&
    (async (inputValue: string, optionLabel?: ReactNode) => {
      try {
        const confirmed = await new Promise<boolean | undefined>((res) => {
          if (!creatingRequiresConfirmation) return res(undefined);

          showModal(area, (close) => (
            <Modal
              isOpen
              toClose={() => {
                res(false);
                close();
              }}
            >
              <ConfirmationModal
                message={creatingConfirmationMessage || 'Are you sure?'}
                onConfirm={() => {
                  res(true);
                  close();
                }}
                onCancel={() => {
                  res(false);
                  close();
                }}
              />
            </Modal>
          ));
        });

        let option: _Option | void = await pOnCreateOption?.(inputValue, confirmed);

        if (confirmed) {
          option ||= getNewOptionData?.(inputValue, optionLabel, confirmed);
          option ||= { label: inputValue, value: inputValue } as _Option;
        }

        if (!option) return;

        // @ts-ignore
        const value = selectRef.current?.getValue();
        // @ts-ignore
        selectRef.current?.onChange(
          // @ts-ignore
          selectRef.current?.props.isMulti
            ? // @ts-ignore
              [...cleanValue(value), option]
            : option,
          { action: 'create-option', name: name, option },
        );
        // eslint-disable-next-line no-empty
      } catch (error) {
        logger.error('Error creating option:', error);
      }
    });

  return { onCreateOption };
}
